import { useDisclosure } from '@chakra-ui/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  apiGetMarketCredentials,
  apiUpdateBrokerCredential,
  type MarketCredentialsData,
  type MarketCredential,
} from 'api/quote-marketplace';

const POLL_INTERVAL = 2000;

interface UseMarketCredentialsProps {
  quoteId: number;
  enabled?: boolean;
}

interface UseMarketCredentialsReturn {
  data: MarketCredentialsData | undefined;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  syncStatus: 'syncing' | 'ready' | 'error' | undefined;
  syncErrorMessage: string | null;
  carriers: MarketCredential[];
  hasCredentialErrors: boolean;
  credentialErrorCarriers: string[];
  refetch: () => void;
  updateCredential: (
    providerCode: string,
    username: string,
    password: string,
  ) => Promise<{ success: boolean; error?: string }>;
  isUpdating: boolean;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
}

export const useMarketCredentials = ({
  quoteId,
  enabled = true,
}: UseMarketCredentialsProps): UseMarketCredentialsReturn => {
  const queryClient = useQueryClient();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const queryKey = ['market-credentials', quoteId];

  const { data, isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: () => apiGetMarketCredentials(quoteId),
    enabled: enabled && quoteId > 0,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchInterval: (query) => {
      const queryData = query.state.data;
      if (queryData?.status === 'syncing') {
        return POLL_INTERVAL;
      }
      const hasUpdating = queryData?.carriers?.some(
        (c) => c.update_status === 'updating',
      );
      return hasUpdating ? POLL_INTERVAL : false;
    },
    structuralSharing: (oldData, newData) => {
      if (!oldData || !newData) return newData;
      const typedOld = oldData as MarketCredentialsData;
      const typedNew = newData as MarketCredentialsData;

      const updatingProviders = new Set(
        typedOld.carriers
          ?.filter((c) => c.update_status === 'updating')
          .map((c) => c.provider_code) ?? [],
      );

      if (updatingProviders.size === 0) return newData;

      return {
        ...typedNew,
        carriers: typedNew.carriers?.map((carrier) => {
          if (
            updatingProviders.has(carrier.provider_code) &&
            !carrier.update_status
          ) {
            return { ...carrier, update_status: 'updating' as const };
          }
          return carrier;
        }),
      };
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      providerCode,
      username,
      password,
    }: {
      providerCode: string;
      username: string;
      password: string;
    }) => apiUpdateBrokerCredential(providerCode, { username, password }),
    onSuccess: (result, variables) => {
      if (result.status === 'updating') {
        queryClient.setQueryData<MarketCredentialsData>(queryKey, (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            carriers: oldData.carriers.map((carrier) =>
              carrier.provider_code === variables.providerCode
                ? {
                    ...carrier,
                    update_status: 'updating' as const,
                    username: variables.username,
                    has_credentials: true,
                  }
                : carrier,
            ),
          };
        });
      }
    },
  });

  const carriers = data?.carriers ?? [];

  const credentialErrorCarriers = carriers
    .filter((c) => c.has_credential_error)
    .map((c) => c.name);

  const hasCredentialErrors = credentialErrorCarriers.length > 0;

  const updateCredential = async (
    providerCode: string,
    username: string,
    password: string,
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      await updateMutation.mutateAsync({
        providerCode,
        username,
        password,
      });
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Unknown error',
      };
    }
  };

  const isFetchingCredentials = isLoading && !data;

  return {
    data,
    isLoading,
    isFetching: isFetchingCredentials,
    error: error as Error | null,
    syncStatus: data?.status,
    syncErrorMessage: data?.error_message ?? null,
    carriers,
    hasCredentialErrors,
    credentialErrorCarriers,
    refetch: () => refetch(),
    updateCredential,
    isUpdating: updateMutation.isPending,
    isOpen,
    onOpen,
    onClose,
  };
};
